<div class="popup-container">
  <h2 mat-dialog-title>{{ title }}</h2>

  <mat-dialog-content>
    <div class="search">
      <mat-form-field appearance="outline" class="search-by-input">
        <mat-label>Search By</mat-label>
        <mat-select [(ngModel)]="filterByOptionSelected" id="search-by-filter-select">
          @for (option of searchByOptions; track option.value) {
            <mat-option [value]="option.value">{{ option.label }}</mat-option>
          }
        </mat-select>
      </mat-form-field>
      <mat-form-field appearance="outline" class="search-input-wrapper">
        <mat-label>Simulation search</mat-label>
        <input
          matInput
          [(ngModel)]="searchString"
          (ngModelChange)="onSearchStringChange()"
        />
        @if (searchString) {
          <button
            mat-icon-button
            matSuffix
            (click)="onClearSearch()"
            aria-label="Clear"
          >
            <mat-icon class="clear-icon">close</mat-icon>
          </button>
        }
      </mat-form-field>
      <button color="primary" class="btn btn-primary btn-big search-btn" (click)="onSearch()">
        <mat-icon>search</mat-icon>
        <span>Search</span>
      </button>
      <button mat-flat-button color="secondary" class="filter-btn" (click)="openFilterPopup()">
        <mat-icon>filter_list</mat-icon>
      </button>
    </div>

    @if (isLoading) {
      <div class="spinner-container">
        <mat-spinner></mat-spinner>
      </div>
    } @else if (simulations.length === 0) {
      <div class="no-data-message">
        <mat-icon>info</mat-icon>
        <p>{{ emptyMessage }}</p>
      </div>
    } @else {
      <div class="table-container">
        <table mat-table [dataSource]="simulations" class="options-table">
          <!-- Radio Button Column -->
          <ng-container matColumnDef="select">
            <th mat-header-cell *matHeaderCellDef>Select</th>
            <td mat-cell *matCellDef="let simulation">
              <mat-radio-button
                [checked]="selectedSimulation === simulation"
                (change)="selectSimulation(simulation)"
              ></mat-radio-button>
            </td>
          </ng-container>

          <!-- Simulation Name Column -->
          <ng-container matColumnDef="simulationName">
            <th mat-header-cell *matHeaderCellDef>Name</th>
            <td mat-cell *matCellDef="let simulation">{{ simulation.simulationName }}</td>
          </ng-container>

          <!-- Rock File Column -->
          <ng-container matColumnDef="rockFile">
            <th mat-header-cell *matHeaderCellDef>Rock File</th>
            <td mat-cell *matCellDef="let simulation">{{ simulation.fileName | removeExtension }}</td>
          </ng-container>

          <!-- Direction Column -->
          <ng-container matColumnDef="direction">
            <th mat-header-cell *matHeaderCellDef>Direction</th>
            <td mat-cell *matCellDef="let simulation">
              @if (simulation.results.length > 0) {
                <mat-form-field appearance="outline" class="compact-form-field">
                  <mat-select
                    [(ngModel)]="simulation.selectedDirection"
                    (selectionChange)="onDirectionChange(simulation)"
                    [disabled]="selectedSimulation !== simulation"
                  >
                    @for (direction of getAvailableDirections(simulation); track direction) {
                      <mat-option [value]="direction" [disabled]="isDirectionInvalid(simulation, direction)">{{ direction }}</mat-option>
                    }
                  </mat-select>
                </mat-form-field>
              } @else {
                <span>-</span>
              }
            </td>
          </ng-container>

          <!-- Cube Size Column (cube trend only) -->
          <ng-container matColumnDef="cubeSize">
            <th mat-header-cell *matHeaderCellDef>Cube size</th>
            <td mat-cell *matCellDef="let simulation">
              @if (simulation.selectedDirection) {
                <mat-form-field appearance="outline" class="compact-form-field">
                  <mat-select
                    [(ngModel)]="simulation.selectedCubeSize"
                    (selectionChange)="onCubeSizeChange(simulation)"
                    [disabled]="selectedSimulation !== simulation"
                    [compareWith]="compareCubeSize"
                  >
                    @for (cubeSize of getAvailableCubeSizes(simulation); track cubeSize.cubeSizeVoxels) {
                      <mat-option [value]="cubeSize">{{ getCubeSizeDisplay(cubeSize) }}</mat-option>
                    }
                  </mat-select>
                </mat-form-field>
              } @else {
                <span>-</span>
              }
            </td>
          </ng-container>

          <!-- Angular Coefficient Column (cube trend only) -->
          <ng-container matColumnDef="angularCoefficient">
            <th mat-header-cell *matHeaderCellDef>Angular<br>Coeff.</th>
            <td mat-cell *matCellDef="let simulation">
              @if (simulation.angularCoefficient !== null) {
                {{ toScientificNotation(simulation.angularCoefficient) }}
              } @else {
                <span>-</span>
              }
            </td>
          </ng-container>

          <!-- Exponential Coefficient Column (cube trend only) -->
          <ng-container matColumnDef="exponentialCoefficient">
            <th mat-header-cell *matHeaderCellDef>Exp.<br>Coeff</th>
            <td mat-cell *matCellDef="let simulation">
              @if (simulation.exponentialCoefficient !== null) {
                {{ simulation.exponentialCoefficient | number : '1.1-1' }}
              } @else {
                <span>-</span>
              }
            </td>
          </ng-container>

          <!-- Connected Porosity Column (subsample only) -->
          <ng-container matColumnDef="connectedPorosity">
            <th mat-header-cell *matHeaderCellDef>Connected<br/>Porosity</th>
            <td mat-cell *matCellDef="let simulation">
              @if (simulation.connectedPorosity !== null) {
                {{ formatPorosity(simulation.connectedPorosity) }}%
              } @else {
                <span>-</span>
              }
            </td>
          </ng-container>

          <!-- Absolute Permeability Column (subsample only) -->
          <ng-container matColumnDef="absolutePermeability">
            <th mat-header-cell *matHeaderCellDef>Absolute<br/>Permeability<br/>(mD)</th>
            <td mat-cell *matCellDef="let simulation">
              @if (simulation.absolutePermeability !== null) {
                {{ formatPermeability(simulation.absolutePermeability) }}
              } @else {
                <span>-</span>
              }
            </td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
          <tr
            mat-row
            *matRowDef="let row; columns: displayedColumns;"
            [class.selected-row]="selectedSimulation === row"
          ></tr>
        </table>
      </div>
    }
  </mat-dialog-content>

  <mat-dialog-actions align="end">
    <button mat-button (click)="onCancel()">
      Cancel
    </button>
    <button
      mat-raised-button
      color="primary"
      (click)="onSelect()"
      [disabled]="isConfirmDisabled"
    >
      Confirm
    </button>
  </mat-dialog-actions>
</div>